import type {
  AssessmentAttachmentResponse,
  MySlcpAssessmentsResponse,
  SlcpAssessmentDetail,
  SlcpAssessmentsParams,
  SlcpAssessmentsResponse,
  SlcpDataPointDef,
} from '@/api/slcp/types';
import { API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type { SlcpSuggestedCapasResponse } from './types';

export async function fetchMySlcpAssessments(
  qs: string,
): Promise<MySlcpAssessmentsResponse> {
  return apiFetch<MySlcpAssessmentsResponse>(
    `/slcp/assessments?${qs}`,
    {},
    BACKEND_URL,
  );
}

export async function archiveSlcpAssessments(
  assessmentIds: string[],
): Promise<void> {
  return apiFetch<void>(
    '/slcp/assessments/archive',
    { method: 'POST', body: JSON.stringify({ assessmentIds }) },
    BACKEND_URL,
  );
}

export async function unarchiveSlcpAssessments(
  assessmentIds: string[],
): Promise<void> {
  return apiFetch<void>(
    '/slcp/assessments/archive',
    { method: 'DELETE', body: JSON.stringify({ assessmentIds }) },
    BACKEND_URL,
  );
}

/**
 * `POST /api/slcp/assessments/fetch` — queues a fetch of the company's
 * assessments from the SLCP gateway (202); they arrive asynchronously.
 */
export async function triggerGatewayAssessmentFetch(): Promise<void> {
  await apiFetch<unknown>(
    '/api/slcp/assessments/fetch',
    { method: 'POST' },
    API_ROOT_URL,
  );
}

/**
 * `GET /api/slcp/assessments/:id/attachment` — the SLCP gateway's report link.
 * Takes the assessment id, not the SLCP object id. Answers `{ metadata, data }`.
 */
export async function fetchAssessmentAttachmentLink(
  assessmentId: string,
): Promise<{ data: AssessmentAttachmentResponse }> {
  return apiFetch<{ data: AssessmentAttachmentResponse }>(
    `/api/slcp/assessments/${assessmentId}/attachment`,
    {},
    API_ROOT_URL,
  );
}

/** `GET /api/slcp/assessments/:id` — answers `{ metadata, data }`. */
export async function fetchAssessmentById(
  assessmentId: string,
): Promise<SlcpAssessmentDetail> {
  const { data } = await apiFetch<{ data: SlcpAssessmentDetail }>(
    `/api/slcp/assessments/${assessmentId}`,
    {},
    API_ROOT_URL,
  );
  return data;
}

/** The data-point catalogue, read for its `externalIds` so an SLCP answer can be
 *  matched to the profile field it fills. Same route as the questionnaire
 *  domain's `fetchAllDataPoints`, kept separate only because the two declare
 *  different views of the definition. Answers `{ metadata, data }`. */
export async function fetchProfileDataPoints(): Promise<SlcpDataPointDef[]> {
  const res = await apiFetch<{ data: SlcpDataPointDef[] }>(
    '/api/questionnaires/data-points',
    {},
    API_ROOT_URL,
  );
  return res?.data ?? [];
}

export async function fetchSlcpSuggestedCapas(
  assessmentId: string,
  body: { page: number; limit: number; filters: Record<string, unknown> },
): Promise<SlcpSuggestedCapasResponse> {
  return apiFetch<SlcpSuggestedCapasResponse>(
    `/slcp/assessments/${assessmentId}/suggested-capas`,
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/**
 * `POST /api/slcp/assessments/:id/history` — records the sync. The companies
 * are derived server-side, so nothing else is needed.
 */
export async function createSlcpAssessmentHistory(body: {
  assessmentId: string;
}): Promise<void> {
  await apiFetch<unknown>(
    `/api/slcp/assessments/${body.assessmentId}/history`,
    { method: 'POST', body: JSON.stringify({}) },
    API_ROOT_URL,
  );
}

/**
 * Cross-feature SLCP endpoints. Used by both the SLCP feature and the
 * network company-profile SLCP tab via the shared SlcpTable component.
 */

export async function fetchSlcpAssessments(
  params: SlcpAssessmentsParams,
): Promise<SlcpAssessmentsResponse> {
  const body = {
    page: params.page,
    limit: params.limit,
    ...(params.filter && Object.keys(params.filter).length > 0
      ? { filter: params.filter }
      : {}),
  };

  return apiFetch<SlcpAssessmentsResponse>(
    '/api/slcp/assessments/network-list',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

export async function requestAccessToAssessment(body: {
  assessment_id: string;
}): Promise<void> {
  return apiFetch<void>(
    '/api/slcp/assessments/requests',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

export async function requestMissingAccessToAssessment(body: {
  companyIds: string[];
}): Promise<void> {
  return apiFetch<void>(
    '/api/slcp/assessments/missing-access-request',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

export async function checkFacilityEligibility(body: {
  facilityId?: string;
}): Promise<{ data: { eligible: boolean; facilityId: string } }> {
  return apiFetch<{ data: { eligible: boolean; facilityId: string } }>(
    '/api/slcp/facilities/eligibility',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}
